import { useState, useEffect, useCallback, useRef } from 'react';
import { offlineStorage, OfflineDataState } from '@/services/offlineStorage';
import { useConnection } from '@/contexts/ConnectionContext'; // Utiliser le contexte global

export interface UseOfflineReturn {
  isOnline: boolean;
  isOfflineMode: boolean;
  offlineData: OfflineDataState;
  setOfflineMode: (enabled: boolean) => Promise<void>;
  toggleOfflineMode: () => Promise<void>;
  refreshOfflineData: () => Promise<void>;
}

export const useOffline = (): UseOfflineReturn => {
  const { isOnline } = useConnection();
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [offlineData, setOfflineData] = useState<OfflineDataState>({
    lastSync: null,
    pendingChanges: 0,
    syncStatus: 'idle',
    isOfflineMode: false
  });

  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Initialisation du service offline
  const initializeOfflineService = useCallback(async () => {
    if (isInitializedRef.current) return;
    
    try {
      await offlineStorage.init();
      
      // Récupérer l'état du mode offline
      const savedOfflineMode = await offlineStorage.getOfflineMode();
      setIsOfflineMode(savedOfflineMode);
      
      // Récupérer les métadonnées de synchronisation
      const lastSync = await offlineStorage.getLastSyncDate();
      const pendingChanges = await offlineStorage.getPendingChangesCount();
      
      setOfflineData(prev => ({
        ...prev,
        lastSync,
        pendingChanges,
        isOfflineMode: savedOfflineMode
      }));
      
      isInitializedRef.current = true;
    } catch (error) {
      console.error('❌ Erreur initialisation service offline:', error);
    }
  }, []);

  // Mise à jour des données offline
  const refreshOfflineData = useCallback(async () => {
    try {
      const lastSync = await offlineStorage.getLastSyncDate();
      const pendingChanges = await offlineStorage.getPendingChangesCount();
      
      setOfflineData(prev => ({
        ...prev,
        lastSync,
        pendingChanges
      }));
    } catch (error) {
      console.error('❌ Erreur mise à jour données offline:', error);
    }
  }, []);

  // Gestion du mode offline manuel
  const setOfflineMode = useCallback(async (enabled: boolean) => {
    try {
      await offlineStorage.setOfflineMode(enabled);
      setIsOfflineMode(enabled);
      
      setOfflineData(prev => ({
        ...prev,
        isOfflineMode: enabled
      }));
      
      
      // Si on désactive le mode offline et qu'on est en ligne, déclencher une sync
      if (!enabled && isOnline) {
        // Émettre un événement personnalisé pour déclencher la synchronisation
        window.dispatchEvent(new CustomEvent('offline-mode-disabled'));
      }
    } catch (error) {
      console.error('❌ Erreur changement mode offline:', error);
    }
  }, [isOnline]);

  // Basculement du mode offline
  const toggleOfflineMode = useCallback(async () => {
    await setOfflineMode(!isOfflineMode);
  }, [isOfflineMode, setOfflineMode]);

  // Gestion des changements de connectivité
  useEffect(() => {
    // Si on revient en ligne et qu'on était en mode offline automatique, désactiver le mode offline
    if (isOnline && isOfflineMode) {
      setOfflineMode(false);
      
      // Si il y a des changements en attente, proposer la synchronisation
      if (offlineData.pendingChanges > 0) {
        window.dispatchEvent(new CustomEvent('connection-restored', {
          detail: { pendingChanges: offlineData.pendingChanges }
        }));
      }
    }

    // Si on perd la connexion et qu'on n'est pas déjà en mode offline manuel, activer automatiquement
    if (!isOnline && !isOfflineMode) {
      setOfflineMode(true);
    }
  }, [isOnline, isOfflineMode, offlineData.pendingChanges, setOfflineMode]);

  // Initialisation au montage du composant
  useEffect(() => {
    initializeOfflineService();
  }, [initializeOfflineService]);

  // Mise à jour périodique des données offline
  useEffect(() => {
    if (isOfflineMode || !isOnline) {
      // Mettre à jour toutes les 30 secondes quand on est offline
      syncIntervalRef.current = setInterval(refreshOfflineData, 30000);
    } else {
      // Mettre à jour toutes les 5 minutes quand on est online
      syncIntervalRef.current = setInterval(refreshOfflineData, 300000);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isOfflineMode, isOnline, refreshOfflineData]);

  // Écouter les événements de synchronisation
  useEffect(() => {
    const handleSyncStart = () => {
      setOfflineData(prev => ({
        ...prev,
        syncStatus: 'syncing'
      }));
    };

    const handleSyncSuccess = () => {
      setOfflineData(prev => ({
        ...prev,
        syncStatus: 'success',
        lastSync: new Date(),
        pendingChanges: 0
      }));
      
      // Rafraîchir les données après un délai
      setTimeout(refreshOfflineData, 1000);
    };

    const handleSyncError = () => {
      setOfflineData(prev => ({
        ...prev,
        syncStatus: 'error'
      }));
    };

    const handleSyncComplete = () => {
      setOfflineData(prev => ({
        ...prev,
        syncStatus: 'idle'
      }));
    };

    // Écouter les événements de synchronisation
    window.addEventListener('sync-start', handleSyncStart);
    window.addEventListener('sync-success', handleSyncSuccess);
    window.addEventListener('sync-error', handleSyncError);
    window.addEventListener('sync-complete', handleSyncComplete);

    return () => {
      window.removeEventListener('sync-start', handleSyncStart);
      window.removeEventListener('sync-success', handleSyncSuccess);
      window.removeEventListener('sync-error', handleSyncError);
      window.removeEventListener('sync-complete', handleSyncComplete);
    };
  }, [refreshOfflineData]);

  // Écouter les changements dans la queue de synchronisation
  useEffect(() => {
    const handleSyncQueueChange = () => {
      refreshOfflineData();
    };

    window.addEventListener('sync-queue-changed', handleSyncQueueChange);

    return () => {
      window.removeEventListener('sync-queue-changed', handleSyncQueueChange);
    };
  }, [refreshOfflineData]);

  return {
    isOnline,
    isOfflineMode,
    offlineData,
    setOfflineMode,
    toggleOfflineMode,
    refreshOfflineData
  };
};

// Hook pour vérifier si une fonctionnalité est disponible offline
export const useOfflineCapability = (feature: string): boolean => {
  const { isOfflineMode, isOnline } = useOffline();
  
  // Fonctionnalités disponibles en mode offline
  const offlineCapabilities = {
    'pesee-creation': true,
    'pesee-viewing': true,
    'ticket-creation': true,
    'ticket-viewing': true,
    'reference-data': true,
    'statistics': false, // Nécessite des calculs serveur
    'reports': false,   // Nécessite des calculs serveur
    'user-management': false, // Nécessite des permissions serveur
    'settings': false   // Nécessite des permissions serveur
  };

  if (isOnline && !isOfflineMode) {
    return true; // Toutes les fonctionnalités disponibles en ligne
  }

  return offlineCapabilities[feature as keyof typeof offlineCapabilities] || false;
};

// Hook pour obtenir des informations détaillées sur l'état offline
export const useOfflineStatus = () => {
  const { isOnline, isOfflineMode, offlineData } = useOffline();
  
  const getStatusMessage = () => {
    if (isOnline && !isOfflineMode) {
      return 'Mode en ligne - Toutes les fonctionnalités disponibles';
    }
    
    if (isOfflineMode) {
      return `Mode offline manuel - ${offlineData.pendingChanges} changements en attente`;
    }
    
    if (!isOnline) {
      return 'Mode offline automatique - Connexion perdue';
    }
    
    return 'État de connexion indéterminé';
  };

  const getStatusColor = () => {
    if (isOnline && !isOfflineMode) return 'green';
    if (isOfflineMode) return 'orange';
    if (!isOnline) return 'red';
    return 'gray';
  };

  const canSync = () => {
    return isOnline && offlineData.pendingChanges > 0;
  };

  return {
    statusMessage: getStatusMessage(),
    statusColor: getStatusColor(),
    canSync: canSync(),
    isOnline,
    isOfflineMode,
    pendingChanges: offlineData.pendingChanges,
    lastSync: offlineData.lastSync
  };
};
